import axios from 'axios'

type EnrollmentErrorBody = {
  message?: string
  error?: string
  errorCode?: string
  errorMessage?: string
  lastError?: string
  validationErrors?: string[]
}

const friendlyByCode: Record<string, string> = {
  ACTIVE_ENROLLMENT_EXISTS:
    'An active enrollment already exists for this student. Please contact us to discuss your options.',
  ESL_SCORE_TOO_LOW:
    'You must score 75% or higher on the English proficiency test before you can enroll.',
  MISSING_REQUIRED_FIELDS:
    'Some required information is missing before we can prepare your enrollment agreement. Please review your personal details and try again.',
  NO_PRICELIST_FOUND:
    'No self-enrollment programs are currently available for this location. Please call us for help.',
  NO_ELIGIBLE_PROGRAMS_FOUND:
    'No eligible self-enrollment programs are currently available for this location. Please call us for help.',
  CONTACT_UPSERT_FAILED:
    'We could not save your student profile. Please review your information or call us for help.',
  ENROLLMENT_CREATION_FAILED:
    'We could not create your enrollment. Please try again or call us for help.',
  SALESFORCE_APEX_ERROR:
    'We could not prepare your enrollment agreement. Please try again or call us for help.',
  BAD_REQUEST:
    'We could not process this enrollment request. Please review your information and try again.',
  NOT_FOUND:
    'We could not find this enrollment session. Please restart or call us for help.'
}

const extractJsonErrorCode = (message: string) => {
  const match = message.match(/"errorCode"\s*:\s*"([^"]+)"/)
  return match?.[1] ?? null
}

const cleanApexMessage = (message: string) => {
  const nestedCode = extractJsonErrorCode(message)
  if (nestedCode && friendlyByCode[nestedCode]) {
    return friendlyByCode[nestedCode]
  }

  if (/Too many SOQL queries/i.test(message)) {
    return 'We found matching student records that need review before we can prepare your enrollment agreement. Please call us for help.'
  }

  if (/ACTIVE_ENROLLMENT_EXISTS|active enrollment already exists/i.test(message)) {
    return friendlyByCode.ACTIVE_ENROLLMENT_EXISTS
  }

  if (/MISSING_REQUIRED_FIELDS|Missing required fields/i.test(message)) {
    return friendlyByCode.MISSING_REQUIRED_FIELDS
  }

  if (/ESL_SCORE_TOO_LOW|ESL score/i.test(message)) {
    return friendlyByCode.ESL_SCORE_TOO_LOW
  }

  if (/before requesting signature/i.test(message) && /ASSESSMENT_FAILED/i.test(message)) {
    return 'You need to pass the English proficiency test before signing your enrollment documents.'
  }

  if (/before requesting signature/i.test(message) && /DOCUMENTS_UPLOADED|DOCUMENTS_PENDING/i.test(message)) {
    return 'Please finish uploading your required documents before signing your enrollment agreement.'
  }

  if (/Apex request failed|System\.|Exception|SOQL|DML failed/i.test(message)) {
    return friendlyByCode.SALESFORCE_APEX_ERROR
  }

  return message
}

const fromBody = (body: EnrollmentErrorBody | string | undefined, fallback: string) => {
  if (typeof body === 'string') {
    return body.trim() ? cleanApexMessage(body) : fallback
  }

  if (!body || typeof body !== 'object') {
    return fallback
  }

  if (body.errorCode && friendlyByCode[body.errorCode]) {
    if (body.errorCode === 'SALESFORCE_APEX_ERROR' && body.errorMessage) {
      return cleanApexMessage(body.errorMessage)
    }
    return friendlyByCode[body.errorCode]
  }

  const message =
    body.errorMessage ??
    body.message ??
    body.error ??
    body.lastError ??
    body.validationErrors?.[0]

  return message ? cleanApexMessage(message) : fallback
}

export const getEnrollmentApiErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    return fromBody(error.response?.data as EnrollmentErrorBody | string | undefined, fallback)
  }

  if (error instanceof Error) {
    return cleanApexMessage(error.message)
  }

  return fallback
}
